import React from 'react'

import { Collapse, Group, ScrollArea, Stack, Text } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { ILoadedScript } from '#state'
import { pluralize, toLowerCaseRoman } from '#util'

import { ExplorerView } from '../ExplorerView/ExplorerView'
import { FaIcon } from '../FaIcon/FaIcon'

import styles from './TitlePageSection.module.scss'

interface TitlePageSectionProps {
  script: ILoadedScript
}

export const TitlePageSection = observer(function TitlePageSection({
  script,
}: TitlePageSectionProps) {
  const { titlePageCount } = script.titlePage
  const hasTitlePage = script.titlePage.hasTitlePages

  const [isCollapsed, setIsCollapsed] = React.useState(!hasTitlePage)
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const selectedPageRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (selectedPageRef.current && viewportRef.current) {
      selectedPageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [script.titlePage.currentTitlePageNumber])

  if (titlePageCount === null) return null

  const handlecreateTitlePage = () => {
    script.createTitlePage()
    setIsCollapsed(false)
  }

  const titlePageHeaderActions = !script.readOnlyByPlan && (
    <ExplorerView.HeaderButton
      icon="fa-plus"
      onClick={handlecreateTitlePage}
      tooltip="Add a title page"
    />
  )

  return (
    <>
      <ExplorerView.Header
        title={`${titlePageCount} title ${pluralize(titlePageCount, 'page')}`}
        buttons={titlePageHeaderActions}
        expandIcon={isCollapsed ? 'point-right' : 'point-down'}
        enableCollapse
        onClick={() => setIsCollapsed(!isCollapsed)}
      />
      <Collapse expanded={!isCollapsed}>
        {hasTitlePage ? (
          <ScrollArea.Autosize
            type="auto"
            mah={250}
            mih={150}
            viewportRef={viewportRef}
          >
            {Array.from({ length: titlePageCount }, (_, i) => {
              const pageNumber = i + 1
              const isSelected =
                script.titlePage.currentTitlePageNumber === pageNumber
              return (
                <Group
                  key={pageNumber}
                  ref={isSelected ? selectedPageRef : null}
                  px="sm"
                  gap="xs"
                  wrap="nowrap"
                  data-selected={isSelected}
                  onClick={() =>
                    script.titlePage.handleTitlePageClick(pageNumber)
                  }
                  className={styles.titlePage_row}
                >
                  <Text
                    flex="1"
                    py="sm"
                    style={{
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                    }}
                  >
                    Title Page {toLowerCaseRoman(pageNumber)}
                  </Text>
                </Group>
              )
            })}
          </ScrollArea.Autosize>
        ) : (
          <Stack px="md" py="lg" gap="md" pb={100}>
            <Text size="md" c="gray.9" fw={600}>
              No title pages yet
            </Text>
            {!script.readOnlyByPlan && (
              <Text size="md" lh={1.2} c="dimmed">
                Create a title page by clicking the{' '}
                <FaIcon
                  span
                  icon="fa-regular fa-file-plus"
                  size="md"
                  pb="2px"
                  mx="4px"
                />{' '}
                button in the top right corner of this panel.
              </Text>
            )}
          </Stack>
        )}
      </Collapse>
    </>
  )
})
